import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, CircularProgress, Modal } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';

// Dialog for choosing which of the account's devices belong to the active custom
// group. Every device on the account is listed with a checkbox; devices already in
// the group start checked. Checking adds, unchecking removes — applied on confirm.
function ManageGroupDevicesModal({
    open,
    onClose,
    groupName,
    groupCompanyId = null,
    devices,
    memberIds,
    onConfirm,
}: any) {
    const { t } = useTranslation(undefined, { keyPrefix: 'RealTime.Groups' });
    const isMobile = useMediaQuery('(max-width: 768px)');

    const [selected, setSelected] = useState<Set<string>>(() => new Set(memberIds));
    const [search, setSearch] = useState('');
    // Filter to only the devices already in the group (for quickly unchecking to remove).
    const [inGroupOnly, setInGroupOnly] = useState(false);
    // True while the membership change is being saved + the grid refetched, so the dialog
    // stays open with a spinner instead of closing before the update lands.
    const [processing, setProcessing] = useState(false);
    // Set once the user toggles a checkbox, so async member updates stop re-seeding.
    const dirtyRef = useRef(false);
    // memberIds is a fresh array each render, and archived devices load into the pool
    // asynchronously (so the member set grows shortly after the dialog opens). A stable
    // string key lets the seed effect react to real membership changes only.
    const memberKey = [...memberIds].sort().join('|');

    // Reset per-open state (search, dirty flag) when the dialog (re)opens for a group.
    useEffect(() => {
        if (open) {
            dirtyRef.current = false;
            setSearch('');
            setInGroupOnly(false);
            setProcessing(false);
        }
    }, [open, groupName]);

    // Seed the checkboxes from the group's members. Re-seed while open and untouched so a
    // member whose device loads after the dialog opened (archived devices arrive via a
    // later fetch) shows checked instead of unchecked — otherwise confirming would diff
    // against the now-complete memberIds and silently drop that member from the group.
    useEffect(() => {
        if (open && !dirtyRef.current) {
            setSelected(new Set(memberIds));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, groupName, memberKey]);

    const normalizedSearch = search.trim().toLowerCase();
    const visibleDevices = useMemo(() => {
        const memberSet = new Set(memberIds);
        const filtered = devices.filter((d: any) => {
            // Only devices in the group's company can be added — the API silently skips
            // cross-company adds (vendor accounts see devices from many companies). Hide
            // the unauthorized ones, but always keep current members visible so they can
            // still be unchecked to remove. Fall open when either company is unknown so
            // single-company accounts (no company_id on the group) are unaffected.
            const authorized =
                memberSet.has(d.deviceId) || !groupCompanyId || d.companyId == null || d.companyId === groupCompanyId;
            if (!authorized) return false;
            if (inGroupOnly && !memberSet.has(d.deviceId)) return false;
            if (normalizedSearch) {
                const haystack = `${d.name ?? ''} ${d.deviceId ?? ''}`.toLowerCase();
                if (!haystack.includes(normalizedSearch)) return false;
            }
            return true;
        });
        // Surface the group's current members at the top so they're easy to review and
        // uncheck. Membership is keyed off the persisted memberIds (not the live checkbox
        // selection), so devices don't jump around as they're checked/unchecked mid-edit.
        // Stable partition — relative order within each partition is preserved.
        const members = filtered.filter((d: any) => memberSet.has(d.deviceId));
        const others = filtered.filter((d: any) => !memberSet.has(d.deviceId));
        return [...members, ...others];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [devices, normalizedSearch, inGroupOnly, memberKey, groupCompanyId]);

    const toggle = (deviceId: string) => {
        dirtyRef.current = true;
        setSelected((prev: Set<string>) => {
            const next = new Set(prev);
            if (next.has(deviceId)) next.delete(deviceId);
            else next.add(deviceId);
            return next;
        });
    };

    // Ignore backdrop/escape closes while a save is in flight.
    const handleClose = () => {
        if (!processing) onClose();
    };

    const handleConfirm = async () => {
        if (processing) return;
        // Convert the final selection into an add/remove delta against current members.
        const memberSet = new Set(memberIds);
        const add = [...selected].filter((id: any) => !memberSet.has(id));
        const remove = memberIds.filter((id: any) => !selected.has(id));
        // Nothing changed — just close without a round-trip.
        if (add.length === 0 && remove.length === 0) {
            onClose();
            return;
        }
        setProcessing(true);
        // onConfirm resolves false on failure so the dialog stays open for a retry.
        const ok = await onConfirm({ add, remove });
        setProcessing(false);
        if (ok !== false) onClose();
    };

    const modalSx = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: isMobile ? '92vw' : '44rem',
        maxWidth: isMobile ? '360px' : 'none',
        maxHeight: '85vh',
        bgcolor: '#FFFFFF',
        borderRadius: isMobile ? '24px' : '1rem',
        boxShadow: '0px 12px 40px rgba(15, 23, 42, 0.25)',
        p: 0,
        overflow: 'hidden',
    };

    return (
        <Modal open={open} onClose={handleClose}>
            <Box sx={modalSx} className='flex flex-col'>
                <div className='flex items-center justify-between border-b border-slate-100 px-5 py-4'>
                    <div className='flex flex-col'>
                        <span className='text-xs font-semibold uppercase tracking-wide text-slate-400'>
                            {t('manageDevices')}
                        </span>
                        <span className='text-2xl font-bold text-slate-700'>{groupName}</span>
                    </div>
                    <button
                        type='button'
                        onClick={handleClose}
                        disabled={processing}
                        aria-label={t('cancel')}
                        className='text-2xl leading-none text-slate-400 transition hover:text-slate-600 disabled:opacity-40'
                    >
                        &times;
                    </button>
                </div>

                <div className='px-5 pt-4'>
                    <input
                        className='w-full rounded-full border border-slate-300 px-4 py-2.5 text-base text-slate-700 focus:border-blue-400 focus:outline-none'
                        placeholder={t('searchDevices')}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                    <div className='mt-3 flex flex-wrap items-center gap-2'>
                        <button
                            type='button'
                            onClick={() => setInGroupOnly((v: boolean) => !v)}
                            className={`rounded-full border px-3 py-1 text-sm font-semibold transition ${
                                inGroupOnly
                                    ? 'border-blue-300 bg-blue-50 text-blue-600'
                                    : 'border-slate-200 text-slate-500 hover:text-slate-700'
                            }`}
                        >
                            {t('inGroupOnly')}
                        </button>
                    </div>
                    <div className='mt-2 flex items-center justify-between px-1 text-sm font-medium text-slate-400'>
                        <span>{t('selectedCount', { count: selected.size })}</span>
                        <span>{t('deviceCount', { count: visibleDevices.length })}</span>
                    </div>
                </div>

                {/* Fixed height so the modal stays the same size regardless of how many
                    devices match the search — it never shrinks as results narrow down. */}
                <div className='mt-2 overflow-y-auto px-5 pb-2' style={{ height: '50vh' }}>
                    {visibleDevices.length === 0 ? (
                        <div className='flex h-full items-center justify-center text-base text-slate-400'>
                            {t('noDevices')}
                        </div>
                    ) : (
                        <ul className='flex flex-col gap-1 py-1'>
                            {visibleDevices.map((device: any) => {
                                const checked = selected.has(device.deviceId);
                                return (
                                    <li key={device.deviceId}>
                                        <label className='flex cursor-pointer items-center gap-3 rounded-xl px-3 py-3 transition hover:bg-slate-50'>
                                            <input
                                                type='checkbox'
                                                checked={checked}
                                                onChange={() => toggle(device.deviceId)}
                                                className='h-5 w-5 flex-shrink-0 accent-blue-500'
                                            />
                                            <span className='flex min-w-0 flex-1 flex-col'>
                                                <span className='truncate text-base font-semibold text-slate-700'>
                                                    {device.name || device.deviceId}
                                                </span>
                                                {device.name ? (
                                                    <span className='truncate text-sm text-slate-400'>
                                                        {device.deviceId}
                                                    </span>
                                                ) : null}
                                            </span>
                                        </label>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>

                <div className='flex justify-end gap-3 border-t border-slate-100 px-5 py-4'>
                    <button
                        type='button'
                        onClick={handleClose}
                        disabled={processing}
                        className='rounded-full border border-slate-300 px-5 py-2.5 text-base font-semibold text-slate-600 transition hover:bg-slate-50 disabled:opacity-50'
                    >
                        {t('cancel')}
                    </button>
                    <button
                        type='button'
                        onClick={handleConfirm}
                        disabled={processing}
                        className='inline-flex items-center gap-2 rounded-full bg-blue-500 px-5 py-2.5 text-base font-semibold text-white shadow-sm transition hover:bg-blue-600 disabled:cursor-not-allowed disabled:bg-blue-400'
                    >
                        {processing && <CircularProgress size={14} thickness={5} sx={{ color: '#ffffff' }} />}
                        {processing ? t('saving') : t('confirm')}
                    </button>
                </div>
            </Box>
        </Modal>
    );
}

export default ManageGroupDevicesModal;
